/**
 * Image attachment helpers for issue reporting (#4303 / ISS-446).
 *
 * Attached, dragged, and pasted images all flow through the existing
 * `screenshotDataUrl` -> `submitIssueReport` callable pipeline. Callable
 * requests are capped at ~10MB, so a raw 12MB phone photo can never be sent
 * as-is: every image is decoded, downscaled, and re-encoded as JPEG first.
 * Anything that cannot be decoded (HEIC in most browsers, corrupt files) or
 * is still too large after compression is rejected with a message the widget
 * shows to the user.
 */

/** Hard cap on the source file before we even try to decode it. */
export const MAX_SOURCE_IMAGE_BYTES = 25 * 1024 * 1024;

/**
 * Cap on the encoded data URL we are willing to send through the callable.
 * The callable request limit is ~10MB and the report also carries the
 * captured context payload, so leave generous headroom.
 */
export const MAX_ENCODED_DATA_URL_BYTES = 7 * 1024 * 1024;

/** Longest edge after downscaling — plenty for reading UI text in a screenshot. */
export const MAX_DIMENSION_PX = 2000;

export const JPEG_QUALITY = 0.85;

export type AttachmentValidation = { ok: true } | { ok: false; reason: string };

function formatMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

/**
 * Cheap pre-decode checks. Returns a user-facing rejection reason on failure.
 */
export function validateImageFile(file: { type: string; size: number; name?: string }): AttachmentValidation {
  const label = file.name ? `"${file.name}"` : "That file";

  if (!file.type || !file.type.startsWith("image/")) {
    return { ok: false, reason: `${label} is not an image. Attach a PNG, JPEG, GIF, or WebP.` };
  }
  if (file.type === "image/svg+xml") {
    return { ok: false, reason: "SVG files are not supported. Attach a PNG, JPEG, GIF, or WebP." };
  }
  if (file.size > MAX_SOURCE_IMAGE_BYTES) {
    return {
      ok: false,
      reason: `${label} is too large (${formatMb(file.size)}). Maximum is ${formatMb(MAX_SOURCE_IMAGE_BYTES)}.`,
    };
  }
  return { ok: true };
}

/**
 * Pull the first image file out of a drop or paste payload.
 * Returns null when the payload carries no image (plain text paste, a
 * non-image file drop) so callers can leave the event alone.
 */
export function firstImageFile(dataTransfer: Pick<DataTransfer, "files"> | null): File | null {
  if (!dataTransfer) return null;
  for (let i = 0; i < dataTransfer.files.length; i++) {
    const file = dataTransfer.files[i];
    if (file.type.startsWith("image/")) return file;
  }
  return null;
}

/**
 * Every image file in a drop, paste or file-picker payload (ISS-485: a report
 * can carry several). Non-image files are left out; an empty list means the
 * payload carried no image.
 */
export function imageFilesFrom(dataTransfer: Pick<DataTransfer, "files"> | null): File[] {
  if (!dataTransfer) return [];
  const files: File[] = [];
  for (let i = 0; i < dataTransfer.files.length; i++) {
    const file = dataTransfer.files[i];
    if (file.type.startsWith("image/")) files.push(file);
  }
  return files;
}

interface Drawable {
  source: CanvasImageSource;
  width: number;
  height: number;
  cleanup: () => void;
}

async function decodeImage(file: Blob): Promise<Drawable> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        cleanup: () => bitmap.close(),
      };
    } catch {
      // fall through to the <img> path — some browsers decode more formats there
    }
  }

  return new Promise<Drawable>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () =>
      resolve({
        source: img,
        width: img.naturalWidth,
        height: img.naturalHeight,
        cleanup: () => URL.revokeObjectURL(url),
      });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read this image. HEIC and other camera-raw formats are not supported — export it as JPEG or PNG and try again."));
    };
    img.src = url;
  });
}

/**
 * Decode -> downscale -> re-encode as a JPEG data URL that fits the callable.
 * Throws an Error with a user-facing message on any failure.
 */
export async function fileToCompressedDataUrl(file: Blob): Promise<string> {
  const drawable = await decodeImage(file);
  try {
    const scale = Math.min(1, MAX_DIMENSION_PX / Math.max(drawable.width, drawable.height));
    const width = Math.max(1, Math.round(drawable.width * scale));
    const height = Math.max(1, Math.round(drawable.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Could not process the image in this browser. Try a different browser or attach a smaller image.");
    }

    // JPEG has no alpha — flatten transparent PNGs onto white, not black.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(drawable.source, 0, 0, width, height);

    const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    if (dataUrl.length > MAX_ENCODED_DATA_URL_BYTES) {
      throw new Error(`Image is still too large after compression (${formatMb(dataUrl.length)}). Attach a smaller image.`);
    }
    return dataUrl;
  } finally {
    drawable.cleanup();
  }
}
